import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, Package, Search } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys } from '~/api/queryKeys';
import { CachedImage } from '~/components/common/cached-image/cached-image';
import { useCompareView } from '~/components/common/enrichment-compare-grid/compare-view-mode-context';
import { EnrichmentCompareGrid } from '~/components/common/enrichment-compare-grid/enrichment-compare-grid';
import { BeforeValueCell } from '~/components/common/enrichment-diff-table/before-value-cell';
import { DiffCell } from '~/components/common/enrichment-diff-table/diff-cell';
import { DiffLegend } from '~/components/common/enrichment-diff-table/diff-legend';
import {
  computeDiffStatus,
  mapProductsById,
} from '~/components/common/enrichment-diff-table/diff-status.util';
import { TitleAccentCell } from '~/components/common/enrichment-diff-table/title-accent-cell';
import { SectionErrorBoundary } from '~/components/common/error-boundary/section-error-boundary';
import { ScoreChip } from '~/components/common/score-chip/score-chip';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { attributes } from '~/constants/config/attribution-list';
import { EnrichmentPhaseBadge } from '~/features/catalog-operations/components/chips';
import { useDebouncedValue } from '~/hooks/use-debounced-value';

import {
  formatFieldLabel,
  snakeToCamel,
  toCompareColumns,
  toCompareColumnsFromScheduled,
  toCompareRows,
  toCompareRowsFromScheduled,
} from '../adapters/compare-grid-adapter';
import { useBeforeEnrichmentProducts } from '../hooks/use-before-enrichment-products';
import { useCompareEdits } from '../hooks/use-compare-edits';
import { useAllExperimentPipelineEnrichedProducts } from '../hooks/use-content-scoring';
import { useScheduledEnrichedProducts } from '../hooks/use-scheduled-enriched-products';
import type { ExperimentPipelineEnrichedProduct } from '../types/content-scoring.types';
import type { PipelineEnrichedProduct } from '../types/scheduled-enrichment.types';
import { getBeforeFieldValue, getBeforeProductId } from '../utils/before-enrichment-field-mapper';

import {
  isSelfTintedField,
  renderCompareValue,
  renderCompareValueFull,
} from './compare-value-renderer';
import { EnrichedFieldCell } from './enriched-field-cell';
import { ENRICHED_FIELD_KEYS, ENRICHED_FIELD_LABELS } from './enriched-field-registry';
import { PaginationControls } from './pagination-controls';
import { PreviewStateRow } from './preview-state-row';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

type ScoreFilter = 'all' | 'scoredOnly';
type SortField = 'title' | 'score';
type SortDirection = 'asc' | 'desc';
interface SortState {
  field: SortField | null;
  direction: SortDirection;
}

interface EnrichmentPreviewProductsProps {
  sessionId: string;
  /**
   * When set (Meta Ads flow), uses GET /enrichments/{workflowId}/pipeline/enriched-products
   * instead of the experiment content-scoring results endpoint. The table layout
   * adapts to the scheduled API's `fields[]` shape.
   */
  workflowId?: string | null;
  /**
   * Render the "Preview Products" section header (icon + title + count).
   * Defaults to `true` for the Enrichment Overview page; the experiment-detail
   * Products tab passes `false` to reuse the table without the section copy.
   */
  showHeader?: boolean;
  /** Reveals the "Before" comparison row under every product when true. Owned by the page, defaults off. */
  viewBefore?: boolean;
  /**
   * GMC/platform connection UUID + catalog snapshot pin, used to fetch pre-enrichment
   * ("before") data. Optional — this component is also rendered from the
   * experiment-detail Products tab, outside the content-enrichment funnel's
   * `EnrichmentCtx.Provider`, so it can't pull these from `useEnrichmentContext()`
   * itself. When omitted, the before-data lookup stays disabled (same as any other
   * table with no viable join key) rather than throwing.
   */
  connectionId?: string;
  syncId?: string | null | undefined;
  /** Called when the user clicks "Retry generation" in the failure banner. */
  onRetry?: () => void;
  /** When true, the retry button shows a loading spinner. */
  isRetrying?: boolean;
}

/**
 * Extracts a numeric score from the pipeline product's `scores` field.
 * Handles both a plain number and an object with an `overall` property.
 */
function extractProductScore(scores: PipelineEnrichedProduct['scores']): number | null {
  if (scores == null) return null;
  if (typeof scores === 'number') return scores;
  if (typeof scores === 'object' && 'overall' in scores) return scores.overall ?? null;
  return null;
}

/** Shared pagination state owned above the error boundary. */
interface PaginationProps {
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** O(1) lookup set of the canonical attribution keys. */
const ATTRIBUTE_SET = new Set<string>(attributes);

const SKELETON_ROWS = 5;
/** Placeholder dynamic-column count during loading; prevents the table collapsing to fixed columns only. */
const SKELETON_FIELD_COLS = 3;

/** Shared pagination footer used by both table variants. */
function PaginationFooter({
  page,
  pageSize,
  totalProducts,
  totalPages,
  resultsLength,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  pageSize: number;
  totalProducts: number;
  totalPages: number;
  resultsLength: number;
  onPageChange: (p: number) => void;
  onPageSizeChange: (s: number) => void;
}) {
  const { t } = useTranslation('content-enrichment');
  const shownStart = totalProducts === 0 ? 0 : page * pageSize + 1;
  const shownEnd = Math.min(page * pageSize + resultsLength, totalProducts);

  return (
    <div className="mt-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-gray-600">
        <span>{t('overview.preview.itemsPerPage')}</span>
        <Select
          value={String(pageSize)}
          onValueChange={(v) => {
            onPageSizeChange(Number(v));
          }}
        >
          <SelectTrigger
            aria-label={t('overview.preview.itemsPerPage')}
            className="h-8 w-20"
            data-testid="enrichment-preview-products-page-size-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent data-testid="enrichment-preview-products-page-size-menu">
            {PAGE_SIZE_OPTIONS.map((n) => (
              <SelectItem
                key={n}
                value={String(n)}
                data-testid={`enrichment-preview-products-page-size-option-${String(n)}`}
              >
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span data-testid="enrichment-preview-products-count">
          {t('overview.preview.pagination', {
            start: shownStart,
            end: shownEnd,
            total: totalProducts,
          })}
        </span>
      </div>

      <PaginationControls
        currentPage={page + 1}
        totalPages={Math.max(1, totalPages)}
        onChange={(next) => {
          onPageChange(next - 1);
        }}
        previousLabel={t('overview.preview.previous')}
        nextLabel={t('overview.preview.next')}
        navLabel={t('overview.preview.paginationNav')}
        testId="enrichment-preview-products-pagination"
      />
    </div>
  );
}

/** Clickable column header that toggles asc/desc sort on `field`, with an icon reflecting current state. */
function SortableHeader({
  label,
  field,
  sort,
  onSortChange,
}: {
  label: string;
  field: SortField;
  sort: SortState;
  onSortChange: (field: SortField) => void;
}) {
  const isActive = sort.field === field;
  const Icon = isActive ? (sort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      type="button"
      className="flex cursor-pointer items-center gap-1 hover:text-gray-900"
      onClick={() => {
        onSortChange(field);
      }}
      aria-label={`Sort by ${label}`}
      data-testid={`enrichment-preview-products-sort-${field}-button`}
    >
      {label}
      <Icon className={`h-3.5 w-3.5 ${isActive ? 'text-gray-900' : 'text-gray-400'}`} />
    </button>
  );
}

/** Search + "scored only" filter toolbar shared above the Preview Products table. */
function PreviewToolbar({
  search,
  onSearchChange,
  scoreFilter,
  onScoreFilterChange,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  scoreFilter: ScoreFilter;
  onScoreFilterChange: (value: ScoreFilter) => void;
}) {
  const { t } = useTranslation('content-enrichment');
  return (
    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="relative w-full max-w-sm">
        <Search
          aria-hidden="true"
          className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400"
        />
        <Input
          value={search}
          onChange={(e) => {
            onSearchChange(e.target.value);
          }}
          placeholder={t('overview.preview.searchPlaceholder')}
          className="pl-9"
          data-testid="enrichment-preview-products-search-input"
          aria-label={t('overview.preview.searchPlaceholder')}
        />
      </div>
      <Select
        value={scoreFilter}
        onValueChange={(v) => {
          onScoreFilterChange(v as ScoreFilter);
        }}
      >
        <SelectTrigger
          aria-label={t('overview.preview.scoreFilterLabel')}
          className="h-9 w-full sm:w-52"
          data-testid="enrichment-preview-products-score-filter-select"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent data-testid="enrichment-preview-products-score-filter-menu">
          <SelectItem value="all" data-testid="enrichment-preview-products-score-filter-option-all">
            {t('overview.preview.scoreFilterAll')}
          </SelectItem>
          <SelectItem
            value="scoredOnly"
            data-testid="enrichment-preview-products-score-filter-option-scored-only"
          >
            {t('overview.preview.scoreFilterScoredOnly')}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Experiment table (content-scoring results API)
// ---------------------------------------------------------------------------

interface ExperimentPreviewTableProps extends PaginationProps {
  sessionId: string;
  showHeader: boolean;
  viewBefore: boolean;
  connectionId?: string;
  syncId?: string | null;
  /** Called when the user clicks "Retry generation" in the failure banner. */
  onRetry?: () => void;
  /** When true, the retry button shows a loading spinner. */
  isRetrying?: boolean;
}

/**
 * Preview Products table for the experiment (GMC) enrichment flow.
 * Fetches GET /experiments/{id}/pipeline/enriched-products and renders dynamic
 * enriched-field columns derived from which fields are non-null across the
 * product set — the same column derivation used in the Review & Refine step.
 */
function ExperimentPreviewTable({
  sessionId,
  showHeader,
  viewBefore,
  connectionId = '',
  syncId = null,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRetry,
  isRetrying,
}: ExperimentPreviewTableProps) {
  const { t } = useTranslation('content-enrichment');

  // Fetch the full product set once — every server page (capped at 200 rows)
  // folded into one response, because sort/filter/search/pagination below all
  // run client-side per product spec.
  const { data, isLoading, isError, error, refetch } =
    useAllExperimentPipelineEnrichedProducts(sessionId);

  const allProducts = useMemo(() => data?.products ?? [], [data?.products]);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 500);
  // Default to all products, sorted highest-score-first so scored rows surface above unscored (0) ones.
  const [scoreFilter, setScoreFilter] = useState<ScoreFilter>('all');
  const [sort, setSort] = useState<SortState>({ field: 'score', direction: 'desc' });

  const handleSortChange = (field: SortField) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, direction: 'asc' };
      return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
    onPageChange(0);
  };

  // Search + "scored only" filter, then sort — all client-side over the full fetched set.
  const filteredProducts = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    let result = allProducts;

    if (query) {
      result = result.filter(
        (p) => p.title.toLowerCase().includes(query) || p.productId.toLowerCase().includes(query),
      );
    }

    if (scoreFilter === 'scoredOnly') {
      result = result.filter((p) => (p.score ?? 0) > 0);
    }

    if (sort.field) {
      const dir = sort.direction === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => {
        if (sort.field === 'score') return ((a.score ?? 0) - (b.score ?? 0)) * dir;
        return a.title.localeCompare(b.title) * dir;
      });
    }

    return result;
  }, [allProducts, debouncedSearch, scoreFilter, sort]);

  const totalProducts = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalProducts / pageSize));
  const products = useMemo(
    () => filteredProducts.slice(page * pageSize, page * pageSize + pageSize),
    [filteredProducts, page, pageSize],
  );

  // Before-data is fetched for the current page regardless of the toggle — cell
  // coloring on the primary row is always-on; `viewBefore` only gates the extra row.
  const externalIds = useMemo(() => products.map((p) => p.externalId), [products]);
  const { data: beforeProducts } = useBeforeEnrichmentProducts({
    connectionId,
    syncId,
    externalIds,
  });
  const beforeById = useMemo(
    () => mapProductsById(beforeProducts ?? [], getBeforeProductId),
    [beforeProducts],
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    onPageChange(0);
  };

  const handleScoreFilterChange = (value: ScoreFilter) => {
    setScoreFilter(value);
    onPageChange(0);
  };

  // Derive visible enriched columns — only fields that are (1) in the global
  // attribution list and (2) non-null in at least one product in the full set.
  const visibleFields = useMemo(
    () =>
      ENRICHED_FIELD_KEYS.filter(
        (k) =>
          ATTRIBUTE_SET.has(k) &&
          allProducts.some((p) => p[k as keyof ExperimentPipelineEnrichedProduct] != null),
      ),
    [allProducts],
  );
  // A product is failed when it has no enriched output across all visible columns.
  const failedCount =
    visibleFields.length > 0
      ? products.filter((p) =>
          visibleFields.every((f) => p[f as keyof ExperimentPipelineEnrichedProduct] == null),
        ).length
      : 0;
  // During initial load use placeholder column count so the table width stays stable.
  const dynamicColCount = isLoading ? SKELETON_FIELD_COLS : visibleFields.length;
  // Fixed columns: Product (sticky) + Lily ID + Score + dynamic enriched fields.
  const totalCols = 3 + dynamicColCount;

  // Compare view — the same page of products and the same before-data, reshaped
  // into feed-vs-enriched rows. Only computed while the compare grid is showing.
  const isCompareMode = useCompareView()?.mode === 'compare';
  // Experiment/GMC flow persists against the default (GMC) enrichment table.
  const { controls: editControls, getEdit } = useCompareEdits(sessionId);
  const compareColumns = useMemo(() => toCompareColumns(visibleFields), [visibleFields]);
  const compareRows = useMemo(
    () => (isCompareMode ? toCompareRows(products, beforeById, compareColumns, { getEdit }) : []),
    [isCompareMode, products, beforeById, compareColumns, getEdit],
  );

  return (
    <div data-testid="enrichment-preview-products">
      {showHeader && (
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
              <Package aria-hidden="true" className="text-primary h-5 w-5" />
            </div>
            <div>
              <h3
                className="text-base font-semibold text-gray-900"
                data-testid="enrichment-preview-products-title"
              >
                {t('overview.preview.sectionTitle')}
              </h3>
              <p
                className="text-sm text-gray-500"
                data-testid="enrichment-preview-products-subtitle"
              >
                {t('overview.preview.sectionSubtitle', { total: totalProducts })}
              </p>
            </div>
          </div>
          <DiffLegend />
        </div>
      )}

      <PreviewToolbar
        search={search}
        onSearchChange={handleSearchChange}
        scoreFilter={scoreFilter}
        onScoreFilterChange={handleScoreFilterChange}
      />

      {/* Partial-failure banner — visible when some products returned no enriched fields */}
      {!isLoading && failedCount > 0 && (
        <div
          className="mt-4 flex items-center justify-between rounded-md border border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700"
          data-testid="enrichment-preview-products-failure-banner"
        >
          <span data-testid="enrichment-preview-products-failure-banner-message">
            {failedCount} of {totalProducts} products failed to generate
          </span>
          {onRetry && (
            <Button
              size="sm"
              onClick={onRetry}
              isLoading={isRetrying ?? false}
              data-testid="enrichment-preview-products-retry-generation-button"
            >
              Retry generation
            </Button>
          )}
        </div>
      )}

      {isCompareMode ? (
        <div className="mt-4" data-testid="enrichment-preview-products-compare">
          <EnrichmentCompareGrid
            columns={compareColumns}
            rows={compareRows}
            renderValue={renderCompareValue}
            renderExpandedValue={renderCompareValueFull}
            editControls={editControls}
            isSelfTintedField={isSelfTintedField}
            isLoading={isLoading}
            isError={isError}
            onRetry={() => {
              void refetch();
            }}
          />
        </div>
      ) : (
        <div className="mt-4 rounded-lg border bg-white">
          <Table
            data-testid="enrichment-preview-products-table"
            className="table-fixed"
            containerClassName="max-h-[520px]"
            style={{ width: `${String(320 + 180 + 180 + 250 * dynamicColCount)}px` }}
          >
            <TableHeader className="sticky top-0 z-20 bg-gray-50">
              <TableRow className="bg-gray-50 hover:bg-gray-50">
                {/* Sticky product column */}
                <TableHead className="sticky left-0 z-30 min-w-[320px] bg-gray-50">
                  <SortableHeader
                    label={t('overview.preview.columns.product')}
                    field="title"
                    sort={sort}
                    onSortChange={handleSortChange}
                  />
                </TableHead>
                {/* product ID */}
                <TableHead className="w-[180px] max-w-[180px]">
                  {t('overview.preview.columns.productId')}
                </TableHead>
                {/* Quality Score */}
                <TableHead className="w-[180px] max-w-[180px]">
                  <SortableHeader
                    label={t('overview.preview.columns.score')}
                    field="score"
                    sort={sort}
                    onSortChange={handleSortChange}
                  />
                </TableHead>
                {/* One column per enriched field present in the result set */}
                {isLoading
                  ? Array.from({ length: SKELETON_FIELD_COLS }, (_, i) => (
                      <TableHead key={`skel-h-${String(i)}`} className="w-[250px] max-w-[250px]">
                        <Skeleton className="h-3 w-20" />
                      </TableHead>
                    ))
                  : visibleFields.map((field) => (
                      <TableHead key={field} className="w-[250px] max-w-[250px]">
                        {ENRICHED_FIELD_LABELS[field]}
                      </TableHead>
                    ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: SKELETON_ROWS }, (_, i) => (
                  <TableRow
                    key={`skel-row-${String(i)}`}
                    data-testid="enrichment-preview-products-loading"
                  >
                    <TableCell className="sticky left-0 z-10 min-w-[320px] bg-white align-top">
                      <div className="flex items-start gap-3">
                        <Skeleton className="h-12 w-12 shrink-0 rounded-md" />
                        <div className="flex-1 space-y-2 pt-1">
                          <Skeleton className="h-3 w-3/4" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="w-[180px] max-w-[180px] align-top">
                      <Skeleton className="h-3 w-24" />
                    </TableCell>
                    {Array.from({ length: SKELETON_FIELD_COLS }, (_, j) => (
                      <TableCell
                        key={`skel-cell-${String(j)}`}
                        className="w-[250px] max-w-[250px] align-top"
                      >
                        <div className="space-y-2">
                          <Skeleton className="h-3 w-full" />
                          <Skeleton className="h-3 w-4/5" />
                          <Skeleton className="h-3 w-2/3" />
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <>
                  <PreviewStateRow
                    colSpan={totalCols}
                    keyPrefix="overview.preview"
                    isLoading={false}
                    isError={isError}
                    isEmpty={products.length === 0}
                    error={error}
                    onRetry={() => {
                      void refetch();
                    }}
                  />
                  {products.map((product) => {
                    const before = beforeById.get(product.externalId);
                    return (
                      <Fragment key={product.productId}>
                        <TableRow
                          data-testid={`enrichment-preview-products-row-${product.productId}`}
                        >
                          {/* Sticky product column — thumbnail + title */}
                          <TableCell className="sticky left-0 z-10 min-w-[320px] overflow-hidden bg-white align-top">
                            <div className="flex items-start gap-3">
                              <CachedImage
                                src={product.optimizedImages?.[0] ?? product.imageLink ?? null}
                                alt=""
                                className="h-12 w-12 shrink-0 rounded-md"
                              />
                              <div className="flex min-w-0 flex-col gap-1">
                                <TitleAccentCell>
                                  <span
                                    className="text-sm text-gray-900"
                                    data-testid={`enrichment-preview-products-row-${product.productId}-title`}
                                  >
                                    {product.title}
                                  </span>
                                </TitleAccentCell>
                                {visibleFields.length > 0 &&
                                  visibleFields.every(
                                    (f) =>
                                      product[f as keyof ExperimentPipelineEnrichedProduct] == null,
                                  ) && (
                                    <span
                                      className="inline-flex w-fit items-center rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-600"
                                      data-testid={`enrichment-preview-products-row-${product.productId}-failed-badge`}
                                    >
                                      Generation failed
                                    </span>
                                  )}
                                {/* Sample-pass origin marker; renders only on `SAMPLE` rows. */}
                                <EnrichmentPhaseBadge
                                  phase={product.enrichmentPhase}
                                  className="w-fit"
                                />
                              </div>
                            </div>
                          </TableCell>

                          {/* Product ID */}
                          <TableCell className="w-[200px] max-w-[200px] overflow-hidden align-top text-sm text-gray-600">
                            <span
                              className="break-all"
                              data-testid={`enrichment-preview-products-row-${product.productId}-product-id`}
                            >
                              {product.productId}
                            </span>
                          </TableCell>

                          {/* Product Score */}
                          <TableCell
                            className="align-top"
                            data-testid={`enrichment-preview-products-row-${product.productId}-score`}
                          >
                            <ScoreChip score={product.score ?? 0} />
                          </TableCell>

                          {/* Dynamic enriched-field columns */}
                          {visibleFields.map((field) => {
                            const status = computeDiffStatus(
                              before ? getBeforeFieldValue(before, field) : undefined,
                              product[field as keyof ExperimentPipelineEnrichedProduct],
                            );
                            return (
                              <TableCell
                                key={field}
                                data-testid={`enrichment-preview-products-row-${product.productId}-${field}`}
                                className="w-[250px] max-w-[250px] overflow-hidden align-top"
                              >
                                {field === 'productHighlight' || field === 'productDetail' ? (
                                  <EnrichedFieldCell
                                    fieldName={field}
                                    value={
                                      product[field as keyof ExperimentPipelineEnrichedProduct]
                                    }
                                    status={status}
                                  />
                                ) : (
                                  <DiffCell status={status}>
                                    <EnrichedFieldCell
                                      fieldName={field}
                                      value={
                                        product[field as keyof ExperimentPipelineEnrichedProduct]
                                      }
                                    />
                                  </DiffCell>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>

                        {/* "Before" comparison row — hidden until the page-level switch is on */}
                        {viewBefore && (
                          <TableRow
                            className="bg-background"
                            data-testid={`enrichment-preview-products-before-row-${product.productId}`}
                          >
                            <TableCell className="bg-background sticky left-0 z-10 min-w-[320px] align-top">
                              <BeforeValueCell
                                value={before ? getBeforeFieldValue(before, 'title') : undefined}
                              />
                            </TableCell>
                            <TableCell className="w-[200px] max-w-[200px] align-top">
                              <Badge variant="outline" className="text-muted-foreground w-fit">
                                {t('diffTable.before.badge')}
                              </Badge>
                            </TableCell>
                            <TableCell className="align-top">
                              <BeforeValueCell
                                value={before ? getBeforeFieldValue(before, 'score') : undefined}
                              />
                            </TableCell>
                            {visibleFields.map((field) => (
                              <TableCell key={field} className="w-[250px] max-w-[250px] align-top">
                                <BeforeValueCell
                                  value={before ? getBeforeFieldValue(before, field) : undefined}
                                  renderValue={(value) => (
                                    <EnrichedFieldCell fieldName={field} value={value} />
                                  )}
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <PaginationFooter
        page={page}
        pageSize={pageSize}
        totalProducts={totalProducts}
        totalPages={totalPages}
        resultsLength={products.length}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Meta table (scheduled pipeline enriched-products API)
// ---------------------------------------------------------------------------

interface MetaPreviewTableProps extends PaginationProps {
  workflowId: string;
  showHeader: boolean;
  viewBefore: boolean;
  connectionId?: string;
  syncId?: string | null;
  /** Called when the user clicks "Retry generation" in the failure banner. */
  onRetry?: () => void;
  /** When true, the retry button shows a loading spinner. */
  isRetrying?: boolean;
}

/**
 * Preview Products table for the Meta Ads scheduled enrichment flow.
 * Fetches GET /enrichments/{workflowId}/pipeline/enriched-products and renders
 * dynamic columns derived from each product's `fields[]` array — one column
 * per enriched attribute (title, description, etc.).
 */
function MetaPreviewTable({
  workflowId,
  showHeader,
  viewBefore,
  connectionId = '',
  syncId = null,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRetry,
  isRetrying,
}: MetaPreviewTableProps) {
  const { t } = useTranslation('content-enrichment');

  const { data, isLoading, isError, error, refetch } = useScheduledEnrichedProducts(
    workflowId,
    page,
    pageSize,
  );

  const products = useMemo(() => data?.products ?? [], [data]);
  const totalProducts = data?.totalProducts ?? 0;
  const totalPages = data?.totalPages ?? 0;

  // Before-data is fetched for the current page regardless of the toggle — cell
  // coloring on the primary row is always-on; `viewBefore` only gates the extra row.
  const externalIds = useMemo(() => products.map((p) => p.externalId), [products]);
  const { data: beforeProducts } = useBeforeEnrichmentProducts({
    connectionId,
    syncId,
    externalIds,
  });
  const beforeById = useMemo(
    () => mapProductsById(beforeProducts ?? [], getBeforeProductId),
    [beforeProducts],
  );

  // Fields excluded from dynamic columns — either rendered as fixed columns
  // elsewhere (title → sticky product cell, image_link / optimized_images →
  // thumbnail) or not suitable for tabular display (retailer_id shown via the
  // product ID column).
  const EXCLUDED_FIELD_NAMES = new Set(['title', 'retailer_id', 'image_link', 'optimized_images']);

  // Derive the column set from the first successful product's field names,
  // skipping anything in the exclusion list.
  const fieldNames: string[] = (
    products.find((p) => p.fields.length > 0)?.fields.map((f) => f.name) ?? []
  ).filter((name) => !EXCLUDED_FIELD_NAMES.has(name));

  // A product is failed when the API reports success=false OR its score is 0 / absent —
  // mirrors the experiment flow which flags products where all enriched fields are null.
  const failedCount = products.filter((p) => !p.success || !p.score).length;
  // During initial load use placeholder column count so the table width stays stable.
  const dynamicFieldCols = isLoading ? SKELETON_FIELD_COLS : fieldNames.length;
  // Fixed columns: Product (sticky) + External ID + Score + dynamic field columns.
  const totalCols = 3 + dynamicFieldCols;

  // Compare view — reshapes this page's products into feed-vs-enriched rows.
  // This endpoint is server-paginated, so counts are page-scoped (isPageScoped).
  const isCompareMode = useCompareView()?.mode === 'compare';
  // Meta scheduled flow persists against the META enrichment table.
  const { controls: editControls, getEdit } = useCompareEdits(workflowId, 'META');
  const compareColumns = useMemo(
    () => toCompareColumnsFromScheduled(fieldNames),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fieldNames is rebuilt each render; key on its contents instead of identity
    [fieldNames.join('|')],
  );
  const compareRows = useMemo(
    () =>
      isCompareMode
        ? toCompareRowsFromScheduled(products, beforeById, compareColumns, { getEdit })
        : [],
    [isCompareMode, products, beforeById, compareColumns, getEdit],
  );

  return (
    <div data-testid="enrichment-preview-products">
      {showHeader && (
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
              <Package aria-hidden="true" className="text-primary h-5 w-5" />
            </div>
            <div>
              <h3
                className="text-base font-semibold text-gray-900"
                data-testid="enrichment-preview-products-title"
              >
                {t('overview.preview.sectionTitle')}
              </h3>
              <p
                className="text-sm text-gray-500"
                data-testid="enrichment-preview-products-subtitle"
              >
                {t('overview.preview.sectionSubtitle', { total: totalProducts })}
              </p>
            </div>
          </div>
          <DiffLegend />
        </div>
      )}

      {/* Partial-failure banner — visible when the API reports per-product failures */}
      {!isLoading && failedCount > 0 && (
        <div
          className="mt-4 flex items-center justify-between rounded-md border border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700"
          data-testid="enrichment-preview-products-failure-banner"
        >
          <span data-testid="enrichment-preview-products-failure-banner-message">
            {failedCount} of {totalProducts} products failed to generate
          </span>
          {onRetry && (
            <Button
              size="sm"
              onClick={onRetry}
              isLoading={isRetrying ?? false}
              data-testid="enrichment-preview-products-retry-generation-button"
            >
              Retry generation
            </Button>
          )}
        </div>
      )}

      {isCompareMode ? (
        <div className="mt-4" data-testid="enrichment-preview-products-compare">
          <EnrichmentCompareGrid
            columns={compareColumns}
            rows={compareRows}
            renderValue={renderCompareValue}
            renderExpandedValue={renderCompareValueFull}
            editControls={editControls}
            isSelfTintedField={isSelfTintedField}
            isLoading={isLoading}
            isError={isError}
            isPageScoped
            onRetry={() => {
              void refetch();
            }}
          />
        </div>
      ) : (
        <div className="mt-4 rounded-lg border bg-white">
          <Table
            data-testid="enrichment-preview-products-table"
            className="table-fixed"
            containerClassName="max-h-[520px]"
            style={{ width: `${String(320 + 250 * (totalCols - 1))}px` }}
          >
            <TableHeader className="sticky top-0 z-20 bg-gray-50">
              <TableRow className="bg-gray-50 hover:bg-gray-50">
                {/* Sticky product column */}
                <TableHead className="sticky left-0 z-30 w-[320px] max-w-[320px] bg-gray-50">
                  {t('overview.preview.columns.product')}
                </TableHead>
                {/* External ID — merchant's own SKU reference */}
                <TableHead className="w-[180px] max-w-[180px]">
                  {t('overview.preview.columns.productId')}
                </TableHead>
                {/* Quality score for this product (0–10) */}
                <TableHead className="w-[100px] max-w-[100px]">
                  {t('overview.preview.columns.score')}
                </TableHead>
                {/* One column per enriched field (imageLink excluded — shown as thumbnail) */}
                {isLoading
                  ? Array.from({ length: SKELETON_FIELD_COLS }, (_, i) => (
                      <TableHead key={`skel-h-${String(i)}`} className="w-[250px] max-w-[250px]">
                        <Skeleton className="h-3 w-20" />
                      </TableHead>
                    ))
                  : fieldNames.map((name) => (
                      <TableHead key={name} className="w-[250px] max-w-[250px]">
                        {formatFieldLabel(name)}
                      </TableHead>
                    ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: SKELETON_ROWS }, (_, i) => (
                  <TableRow
                    key={`skel-row-${String(i)}`}
                    data-testid="enrichment-preview-products-loading"
                  >
                    <TableCell className="sticky left-0 z-10 w-[320px] max-w-[320px] bg-white align-top">
                      <div className="flex items-start gap-3">
                        <Skeleton className="h-12 w-12 shrink-0 rounded-md" />
                        <div className="flex-1 space-y-2 pt-1">
                          <Skeleton className="h-3 w-3/4" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="w-[180px] max-w-[180px] align-top">
                      <Skeleton className="h-3 w-24" />
                    </TableCell>
                    <TableCell className="w-[100px] max-w-[100px] align-top">
                      <Skeleton className="h-5 w-10 rounded-md" />
                    </TableCell>
                    {Array.from({ length: SKELETON_FIELD_COLS }, (_, j) => (
                      <TableCell
                        key={`skel-cell-${String(j)}`}
                        className="w-[250px] max-w-[250px] align-top"
                      >
                        <div className="space-y-2">
                          <Skeleton className="h-3 w-full" />
                          <Skeleton className="h-3 w-4/5" />
                          <Skeleton className="h-3 w-2/3" />
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <>
                  <PreviewStateRow
                    colSpan={totalCols}
                    keyPrefix="overview.preview"
                    isLoading={false}
                    isError={isError}
                    isEmpty={products.length === 0}
                    error={error}
                    onRetry={() => {
                      void refetch();
                    }}
                  />
                  {products.map((product) => {
                    // Build a field-name → value lookup for O(1) column access.
                    // Prefer objectValue (structured shape) > values (string[]) > value (string)
                    // so EnrichedFieldCell receives the richest available representation.
                    const fieldMap = new Map(
                      product.fields.map((f) => [
                        f.name,
                        (f.objectValue ??
                          (f.values?.length ? f.values : null) ??
                          f.value ??
                          null) as unknown,
                      ]),
                    );
                    const rawTitle = fieldMap.get('title');
                    const title = typeof rawTitle === 'string' ? rawTitle : product.externalId;
                    const imageUrl = product.optimizedImages?.[0] ?? product.imageLink ?? null;
                    const score = product.score ?? extractProductScore(product.scores);
                    const before = beforeById.get(product.externalId);

                    return (
                      <Fragment key={product.productId}>
                        <TableRow
                          data-testid={`enrichment-preview-products-row-${product.productId}`}
                        >
                          {/* Sticky product column — thumbnail + title */}
                          <TableCell className="sticky left-0 z-10 w-[320px] max-w-[320px] overflow-hidden bg-white align-top">
                            <div className="flex items-start gap-3">
                              <CachedImage
                                src={imageUrl}
                                alt=""
                                className="h-12 w-12 shrink-0 rounded-md"
                              />
                              <div className="flex min-w-0 flex-col gap-1">
                                <TitleAccentCell>
                                  <span
                                    className="text-sm text-gray-900"
                                    data-testid={`enrichment-preview-products-row-${product.productId}-title`}
                                  >
                                    {title}
                                  </span>
                                </TitleAccentCell>
                                {(!product.success || !product.score) && (
                                  <span
                                    className="inline-flex w-fit items-center rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-600"
                                    data-testid={`enrichment-preview-products-row-${product.productId}-failed-badge`}
                                  >
                                    Generation failed
                                  </span>
                                )}
                                {/* Sample-pass origin marker; renders only on `SAMPLE` rows. */}
                                <EnrichmentPhaseBadge
                                  phase={product.enrichmentPhase}
                                  className="w-fit"
                                />
                              </div>
                            </div>
                          </TableCell>

                          {/* Product ID */}
                          <TableCell className="w-[180px] max-w-[180px] overflow-hidden align-top text-xs text-gray-600">
                            <span
                              className="break-all"
                              data-testid={`enrichment-preview-products-row-${product.productId}-product-id`}
                            >
                              {product.productId}
                            </span>
                          </TableCell>

                          {/* Product quality score */}
                          <TableCell
                            className="w-[100px] max-w-[100px] align-top"
                            data-testid={`enrichment-preview-products-row-${product.productId}-score`}
                          >
                            <ScoreChip score={score ?? NaN} />
                          </TableCell>

                          {/* Dynamic field columns */}
                          {fieldNames.map((name) => {
                            const camelKey = snakeToCamel(name);
                            const afterValue = fieldMap.get(name) ?? null;
                            const status = computeDiffStatus(
                              before ? getBeforeFieldValue(before, camelKey) : undefined,
                              afterValue,
                            );
                            return (
                              <TableCell
                                key={name}
                                data-testid={`enrichment-preview-products-row-${product.productId}-${camelKey}`}
                                className="w-[250px] max-w-[250px] overflow-hidden align-top"
                              >
                                {camelKey === 'productHighlight' || camelKey === 'productDetail' ? (
                                  <EnrichedFieldCell
                                    fieldName={camelKey}
                                    value={afterValue}
                                    status={status}
                                  />
                                ) : (
                                  <DiffCell status={status}>
                                    <EnrichedFieldCell fieldName={camelKey} value={afterValue} />
                                  </DiffCell>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>

                        {/* "Before" comparison row — hidden until the page-level switch is on */}
                        {viewBefore && (
                          <TableRow
                            className="bg-background"
                            data-testid={`enrichment-preview-products-before-row-${product.productId}`}
                          >
                            <TableCell className="bg-background sticky left-0 z-10 w-[320px] max-w-[320px] align-top">
                              <BeforeValueCell
                                value={before ? getBeforeFieldValue(before, 'title') : undefined}
                              />
                            </TableCell>
                            <TableCell className="w-[180px] max-w-[180px] align-top">
                              <Badge variant="outline" className="text-muted-foreground w-fit">
                                {t('diffTable.before.badge')}
                              </Badge>
                            </TableCell>
                            <TableCell className="w-[100px] max-w-[100px] align-top">
                              <BeforeValueCell
                                value={before ? getBeforeFieldValue(before, 'score') : undefined}
                              />
                            </TableCell>
                            {fieldNames.map((name) => {
                              const camelKey = snakeToCamel(name);
                              return (
                                <TableCell key={name} className="w-[250px] max-w-[250px] align-top">
                                  <BeforeValueCell
                                    value={
                                      before ? getBeforeFieldValue(before, camelKey) : undefined
                                    }
                                    renderValue={(value) => (
                                      <EnrichedFieldCell fieldName={camelKey} value={value} />
                                    )}
                                  />
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <PaginationFooter
        page={page}
        pageSize={pageSize}
        totalProducts={totalProducts}
        totalPages={totalPages}
        resultsLength={products.length}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public wrapper (shared entry point)
// ---------------------------------------------------------------------------

/**
 * Public entry point for the Enrichment Overview "Preview Products" tab and
 * the experiment-detail Products tab.
 *
 * When `workflowId` is provided (Meta Ads scheduled flow) the table calls
 * GET /enrichments/{workflowId}/pipeline/enriched-products and adapts its
 * columns to the `fields[]` response shape. Otherwise it falls back to the
 * experiment content-scoring results endpoint.
 *
 * Pagination state lives above the error boundary so a boundary reset (retry)
 * preserves the user's current page position.
 */
export function EnrichmentPreviewProducts({
  sessionId,
  workflowId = null,
  showHeader = true,
  viewBefore = false,
  connectionId,
  syncId,
  onRetry,
  isRetrying,
}: EnrichmentPreviewProductsProps) {
  const { t } = useTranslation('content-enrichment');
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    setPage(0);
  };

  const isMetaMode = !!workflowId;

  return (
    <SectionErrorBoundary
      resetKeys={[sessionId, workflowId]}
      onReset={() => {
        if (isMetaMode) {
          void queryClient.resetQueries({
            queryKey: queryKeys.scheduledEnrichment.pipelineEnrichedProductsAll(workflowId),
          });
        } else {
          void queryClient.resetQueries({
            queryKey: queryKeys.experiments.pipelineEnrichedProductsAll(sessionId),
          });
        }
      }}
      title={t('overview.preview.boundary.title')}
      description={t('overview.preview.boundary.description')}
      retryLabel={t('overview.preview.boundary.retry')}
    >
      {isMetaMode ? (
        <MetaPreviewTable
          workflowId={workflowId}
          showHeader={showHeader}
          viewBefore={viewBefore}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          {...(connectionId !== undefined && { connectionId })}
          {...(syncId !== undefined && { syncId })}
          {...(onRetry && { onRetry })}
          {...(isRetrying !== undefined && { isRetrying })}
        />
      ) : (
        <ExperimentPreviewTable
          sessionId={sessionId}
          showHeader={showHeader}
          viewBefore={viewBefore}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          {...(connectionId !== undefined && { connectionId })}
          {...(syncId !== undefined && { syncId })}
          {...(onRetry && { onRetry })}
          {...(isRetrying !== undefined && { isRetrying })}
        />
      )}
    </SectionErrorBoundary>
  );
}
